import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * ModelsPanel — list / pull / delete / hot-load / keep-alive Ollama
 * models on a registered host (P1-AI07 + P1-AI11 + P1-AI13).
 *
 * Renders as a panel inside HostDetailDrawer. Talks to:
 *   GET    /v1/admin/ai-hosts/:id/ollama/models
 *   POST   /v1/admin/ai-hosts/:id/ollama/pull                          body: { model }
 *   DELETE /v1/admin/ai-hosts/:id/ollama/models/:name
 *   POST   /v1/admin/ai-hosts/:id/ollama/models/:name/hot-load           (P1-AI13)
 *   PATCH  /v1/admin/ai-hosts/:id/ollama/models/:name/keep-alive         (P1-AI13)
 *   GET    /v1/admin/ai-hosts/:id                                       (refresh metadata after PATCH)
 *
 * State machine is intentionally simple — no SSE. The "Hot-load"
 * button posts the endpoint and toasts on completion; the keep-alive
 * toggle PATCHes and updates the in-component cache so subsequent
 * renders reflect the new state.
 *
 * "Browse models" opens BrowseModelsModal (P1-AI11) which renders the
 * /api/tags payload grouped by family.
 *
 * When the host has no baseUrlOllama, the panel shows an "Install
 * Ollama first" hint instead of attempting the proxy call.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { NexusAdminApiError, useNexusFetch } from '../../api.js';
import { BrowseModelsModal } from './BrowseModelsModal.js';
import { formatBytes, readKeepAliveForModel } from './shared.js';
const MODEL_NAME_RE = /^[a-zA-Z0-9][a-zA-Z0-9._:/-]{0,127}$/;
// Default keep_alive (seconds) we send when toggling OFF. Matches
// Ollama's documented daemon default. Sending it explicitly (rather
// than DELETEing the row) gives us an audit trail of the toggle.
const DEFAULT_KEEP_ALIVE_OFF_SECONDS = 30;
export function ModelsPanel({ host, onToast, onModelsLoaded }) {
    const fetcher = useNexusFetch();
    const [models, setModels] = useState(null);
    const [loadError, setLoadError] = useState(null);
    const [pullName, setPullName] = useState('');
    const [pulling, setPulling] = useState(false);
    const [deletingNames, setDeletingNames] = useState(() => new Set());
    const [browseOpen, setBrowseOpen] = useState(false);
    // P1-AI13 — local cache of the host's metadata so the keep-alive
    // toggle reflects the latest PATCH without a full reload. Seeded
    // from the host prop; updated from server responses.
    const [metadata, setMetadata] = useState(host.metadata ?? {});
    // Per-model in-flight state for hot-load + keep-alive toggle so the
    // buttons can show spinners without nuking the whole list.
    const [hotLoadingNames, setHotLoadingNames] = useState(() => new Set());
    const [togglingKeepAliveNames, setTogglingKeepAliveNames] = useState(() => new Set());
    // Track the most-recent successful hot-load timestamp per model so
    // the row can render "Loaded just now" briefly. Cleared on host
    // change.
    const [lastHotLoadedAt, setLastHotLoadedAt] = useState({});
    const canTalk = Boolean(host.baseUrlOllama);
    // Reset local caches when the host changes (parent passed a
    // different host in).
    useEffect(() => {
        setMetadata(host.metadata ?? {});
        setLastHotLoadedAt({});
    }, [host.id, host.metadata]);
    const load = useCallback(async () => {
        if (!canTalk) {
            setModels([]);
            onModelsLoaded?.([]);
            return;
        }
        setLoadError(null);
        try {
            const res = await fetcher(`/v1/admin/ai-hosts/${encodeURIComponent(host.id)}/ollama/models`);
            setModels(res?.models ?? []);
            onModelsLoaded?.((res?.models ?? []).map((m) => m.name));
        }
        catch (e) {
            setModels([]);
            onModelsLoaded?.([]);
            if (e instanceof NexusAdminApiError) {
                setLoadError(`${e.code}: ${e.message}`);
            }
            else {
                setLoadError(e instanceof Error ? e.message : 'failed to load models');
            }
        }
    }, [canTalk, fetcher, host.id, onModelsLoaded]);
    useEffect(() => {
        void load();
    }, [load]);
    const handlePull = useCallback(async () => {
        const model = pullName.trim();
        if (!model) {
            onToast('warning', 'Enter a model name to pull (e.g. llama3.1:8b).');
            return;
        }
        if (!MODEL_NAME_RE.test(model)) {
            onToast('warning', 'Model name has invalid characters.');
            return;
        }
        setPulling(true);
        try {
            await fetcher(`/v1/admin/ai-hosts/${encodeURIComponent(host.id)}/ollama/pull`, { method: 'POST', json: { model } });
            onToast('success', `Pulled "${model}" on ${host.name}.`);
            setPullName('');
            await load();
        }
        catch (e) {
            const msg = e instanceof NexusAdminApiError
                ? `${e.code}: ${e.message}`
                : e instanceof Error
                    ? e.message
                    : 'pull failed';
            onToast('danger', `Pull failed: ${msg}`);
        }
        finally {
            setPulling(false);
        }
    }, [fetcher, host.id, host.name, load, onToast, pullName]);
    const handleDelete = useCallback(async (name) => {
        if (!confirm(`Delete model "${name}" from ${host.name}?`))
            return;
        setDeletingNames((prev) => {
            const next = new Set(prev);
            next.add(name);
            return next;
        });
        try {
            await fetcher(`/v1/admin/ai-hosts/${encodeURIComponent(host.id)}/ollama/models/${encodeURIComponent(name)}`, { method: 'DELETE' });
            onToast('success', `Deleted "${name}" from ${host.name}.`);
            await load();
        }
        catch (e) {
            const msg = e instanceof NexusAdminApiError
                ? `${e.code}: ${e.message}`
                : e instanceof Error
                    ? e.message
                    : 'delete failed';
            onToast('danger', `Delete failed: ${msg}`);
        }
        finally {
            setDeletingNames((prev) => {
                const next = new Set(prev);
                next.delete(name);
                return next;
            });
        }
    }, [fetcher, host.id, host.name, load, onToast]);
    // ── P1-AI13 — hot-load ───────────────────────────────────────────
    const handleHotLoad = useCallback(async (name) => {
        setHotLoadingNames((prev) => {
            const next = new Set(prev);
            next.add(name);
            return next;
        });
        try {
            const res = await fetcher(`/v1/admin/ai-hosts/${encodeURIComponent(host.id)}/ollama/models/${encodeURIComponent(name)}/hot-load`, { method: 'POST' });
            setLastHotLoadedAt((prev) => ({ ...prev, [name]: Date.now() }));
            const seconds = res.loadDurationNs !== null
                ? Math.round(res.loadDurationNs / 1e8) / 10
                : null;
            onToast('success', seconds !== null
                ? `Loaded "${name}" into VRAM in ${seconds}s.`
                : `Loaded "${name}" into VRAM.`);
        }
        catch (e) {
            const msg = e instanceof NexusAdminApiError
                ? `${e.code}: ${e.message}`
                : e instanceof Error
                    ? e.message
                    : 'hot-load failed';
            onToast('danger', `Hot-load failed: ${msg}`);
        }
        finally {
            setHotLoadingNames((prev) => {
                const next = new Set(prev);
                next.delete(name);
                return next;
            });
        }
    }, [fetcher, host.id, onToast]);
    // ── P1-AI13 — keep-alive toggle ──────────────────────────────────
    const handleToggleKeepAlive = useCallback(async (name, on) => {
        setTogglingKeepAliveNames((prev) => {
            const next = new Set(prev);
            next.add(name);
            return next;
        });
        try {
            const body = {
                keepAliveSeconds: on ? -1 : DEFAULT_KEEP_ALIVE_OFF_SECONDS,
            };
            const res = await fetcher(`/v1/admin/ai-hosts/${encodeURIComponent(host.id)}/ollama/models/${encodeURIComponent(name)}/keep-alive`, { method: 'PATCH', json: body });
            // Mirror the change locally so the toggle visibly flips
            // without a refetch. The server may also pull the host row
            // for the host's lastModifiedAt etc., but that's a parent
            // concern.
            setMetadata((prev) => {
                const current = prev.ollamaKeepAliveByModel &&
                    typeof prev.ollamaKeepAliveByModel === 'object' &&
                    !Array.isArray(prev.ollamaKeepAliveByModel)
                    ? {
                        ...prev.ollamaKeepAliveByModel,
                    }
                    : {};
                current[name] = res.keepAliveSeconds;
                return { ...prev, ollamaKeepAliveByModel: current };
            });
            onToast('info', on
                ? `Will keep "${name}" hot indefinitely.`
                : `"${name}" will unload after ~30s idle.`);
            // Best-effort: re-pull the host row so the persisted metadata
            // matches the local mirror after a full page reload. Failure
            // is non-fatal — local cache is authoritative until refresh.
            try {
                const refreshed = await fetcher(`/v1/admin/ai-hosts/${encodeURIComponent(host.id)}`);
                if (refreshed?.host?.metadata) {
                    setMetadata(refreshed.host.metadata);
                }
            }
            catch {
                /* swallow — optimistic state above is still correct */
            }
        }
        catch (e) {
            const msg = e instanceof NexusAdminApiError
                ? `${e.code}: ${e.message}`
                : e instanceof Error
                    ? e.message
                    : 'keep-alive update failed';
            onToast('danger', `Keep-alive update failed: ${msg}`);
        }
        finally {
            setTogglingKeepAliveNames((prev) => {
                const next = new Set(prev);
                next.delete(name);
                return next;
            });
        }
    }, [fetcher, host.id, onToast]);
    const browseDisabled = useMemo(() => !models || models.length === 0, [models]);
    if (!canTalk) {
        return (_jsxs("div", { className: "text-muted small", children: [_jsxs("p", { className: "mb-1", children: ["No ", _jsx("code", { children: "baseUrlOllama" }), " on this host."] }), _jsxs("p", { className: "mb-0", children: ["Run ", _jsx("strong", { children: "Install Ollama" }), " first (or set the URL in the host edit form)."] })] }));
    }
    return (_jsxs("div", { children: [_jsxs("div", { className: "d-flex gap-2 mb-3 align-items-end flex-wrap", children: [_jsxs("div", { className: "flex-grow-1", style: { minWidth: 200 }, children: [_jsx("label", { className: "form-label small mb-1", children: "Pull model" }), _jsx("input", { type: "text", className: "form-control form-control-sm font-monospace", value: pullName, onChange: (e) => setPullName(e.target.value), placeholder: "llama3.1:8b", disabled: pulling, spellCheck: false, autoComplete: "off" })] }), _jsx("button", { type: "button", className: "btn btn-sm btn-primary", onClick: () => void handlePull(), disabled: pulling || !pullName.trim(), children: pulling ? (_jsxs(_Fragment, { children: [_jsx("span", { className: "spinner-border spinner-border-sm me-2", role: "status", "aria-hidden": "true" }), "Pulling\u2026"] })) : ('Pull') }), _jsx("button", { type: "button", className: "btn btn-sm btn-outline-secondary", onClick: () => void load(), disabled: models === null, children: "Refresh" }), _jsx("button", { type: "button", className: "btn btn-sm btn-outline-primary", onClick: () => setBrowseOpen(true), disabled: browseDisabled, title: browseDisabled
                            ? 'No models to browse yet — pull one first'
                            : `Browse all models on ${host.name}`, children: "Browse models" })] }), loadError && (_jsx("div", { className: "alert alert-danger small mb-3", children: loadError.startsWith('OLLAMA_UNREACHABLE') ? (_jsxs(_Fragment, { children: [_jsxs("strong", { children: [host.name, " is not answering on ", host.baseUrlOllama, "."] }), ' ', "Ollama is probably not running there, or it is bound to localhost only \u2014 it must listen on the address above for the gateway to reach it (", _jsx("code", { children: "OLLAMA_HOST=0.0.0.0" }), " on that machine).", _jsx("div", { className: "text-muted mt-1", children: loadError })] })) : (loadError) })), models === null && (_jsxs("div", { className: "text-muted small", children: [_jsx("span", { className: "spinner-border spinner-border-sm me-2", role: "status", "aria-hidden": "true" }), "Loading models from ", host.name, "\u2026"] })), models !== null && models.length === 0 && !loadError && (_jsxs("div", { className: "text-muted small", children: ["No models on ", host.name, " yet. Pull one above (e.g.", ' ', _jsx("code", { children: "llama3.1:8b" }), ")."] })), models !== null && models.length > 0 && (_jsx("div", { className: "table-responsive", children: _jsxs("table", { className: "table table-sm table-striped align-middle mb-0", children: [_jsx("thead", { children: _jsxs("tr", { children: [_jsx("th", { scope: "col", children: "Name" }), _jsx("th", { scope: "col", children: "Size" }), _jsx("th", { scope: "col", children: "Keep hot" }), _jsx("th", { scope: "col", className: "text-end", children: "Actions" })] }) }), _jsx("tbody", { children: models.map((m) => {
                                const deleting = deletingNames.has(m.name);
                                const hotLoading = hotLoadingNames.has(m.name);
                                const togglingKeepAlive = togglingKeepAliveNames.has(m.name);
                                const stored = readKeepAliveForModel(metadata, m.name);
                                const keepHot = stored === -1;
                                const lastLoaded = lastHotLoadedAt[m.name];
                                const recentlyLoaded = lastLoaded !== undefined &&
                                    Date.now() - lastLoaded < 60_000;
                                return (_jsxs("tr", { children: [_jsxs("td", { className: "font-monospace small", children: [m.name, recentlyLoaded && (_jsx("span", { className: "badge bg-success ms-2", title: "Recently hot-loaded into VRAM", children: "loaded" }))] }), _jsx("td", { className: "small", children: formatBytes(m.size) }), _jsx("td", { children: _jsx("div", { className: "form-check form-switch m-0", children: _jsx("input", { type: "checkbox", role: "switch", className: "form-check-input", checked: keepHot, disabled: togglingKeepAlive, onChange: (e) => void handleToggleKeepAlive(m.name, e.target.checked), "aria-label": `Keep ${m.name} hot indefinitely` }) }) }), _jsx("td", { className: "text-end", children: _jsxs("div", { className: "btn-group btn-group-sm", children: [_jsx("button", { type: "button", className: "btn btn-outline-secondary", onClick: () => void handleHotLoad(m.name), disabled: hotLoading || deleting, title: "Load this model into VRAM now", children: hotLoading ? (_jsx("span", { className: "spinner-border spinner-border-sm", role: "status", "aria-hidden": "true" })) : ('Hot-load') }), _jsx("button", { type: "button", className: "btn btn-outline-danger", onClick: () => void handleDelete(m.name), disabled: deleting || hotLoading, children: deleting ? (_jsx("span", { className: "spinner-border spinner-border-sm", role: "status", "aria-hidden": "true" })) : ('Delete') })] }) })] }, m.name));
                            }) })] }) })), browseOpen && models !== null && (_jsx(BrowseModelsModal, { host: host, models: models, onClose: () => setBrowseOpen(false) }))] }));
}
//# sourceMappingURL=ModelsPanel.js.map